import { designTokens } from '@app/theme';
import { Button, Checkbox, Empty, Space, Spin, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PermissionResponse } from '@api/generated/types.gen';

import { groupPermissionsByArea } from '../lib';

const { Text } = Typography;

type PermissionPickerProps = {
  permissions: PermissionResponse[];
  value: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
};

/**
 * Выбор набора прав, сгруппированных по области (`users`, `settings`, …).
 *
 * Группировка и сортировка делаются в `groupPermissionsByArea`; область выводится
 * из имени права, спец-логики по конкретным значениям нет. Работает в терминах
 * id прав (их принимают эндпоинты записи), отображает имя + описание.
 */
export function PermissionPicker({
  permissions,
  value,
  onChange,
  loading = false,
  disabled = false,
}: PermissionPickerProps) {
  const { t } = useTranslation('rbac');
  const groups = useMemo(() => groupPermissionsByArea(permissions), [permissions]);
  const selected = useMemo(() => new Set(value), [value]);
  const allIds = useMemo(
    () => permissions.map((p) => p.id ?? '').filter(Boolean),
    [permissions],
  );

  const toggle = (id: string, checked: boolean): void => {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    onChange([...next]);
  };

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (permissions.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('picker.empty')} />;
  }

  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('picker.selected', { selected: selected.size, total: permissions.length })}
        </Text>
        <Button
          type="link"
          size="small"
          disabled={disabled}
          onClick={() => {
            onChange(allSelected ? [] : allIds);
          }}
        >
          {allSelected ? t('picker.clear') : t('picker.selectAll')}
        </Button>
      </Space>

      {groups.map((group) => (
        <div key={group.area}>
          <Text
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: designTokens.slate500,
            }}
          >
            {group.area}
          </Text>
          <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 10 }}>
            {group.permissions.map((permission) => {
              const id = permission.id ?? '';
              return (
                <Checkbox
                  key={id}
                  checked={selected.has(id)}
                  disabled={disabled}
                  onChange={(e) => {
                    toggle(id, e.target.checked);
                  }}
                  style={{ alignItems: 'flex-start' }}
                >
                  <Space direction="vertical" size={0}>
                    <Text
                      code
                      style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12 }}
                    >
                      {permission.name}
                    </Text>
                    {permission.description && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {permission.description}
                      </Text>
                    )}
                  </Space>
                </Checkbox>
              );
            })}
          </Space>
        </div>
      ))}
    </Space>
  );
}
